import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PageWrapperComponent } from 'app/components/ui-kit/page-components/page-wrapper/page-wrapper.component';
import { UserService } from 'app/services/user.service';
import { imgIxParams } from 'app/constants/img-ix-params';
import { SpinnerComponent } from 'app/components/ui-kit/loaders/spinner/spinner.component';
import { SvgLoaderComponent } from 'app/components/ui-kit/images/svg-loader/svg-loader.component';
import { AlertMessageComponent } from 'app/components/ui-kit/alerts/alert-message/alert-message.component';
import { MembershipBlockComponent } from "../../../components/distinct-components/memberships/membership-block/membership-block.component";
import { BaseInputComponent } from 'app/components/ui-kit/inputs/base-input/base-input.component';
import { PrimaryBtnComponent } from 'app/components/ui-kit/buttons/primary-btn/primary-btn.component';
import { Router } from '@angular/router';
import { ToggleRowComponent } from 'app/components/distinct-components/settings/toggle-row/toggle-row.component';
import { preferenceMeta, preferenceUseMeta } from 'app/constants/preference-meta-contstants';
import { SectionLabelComponent } from 'app/components/ui-kit/typography/section-label/section-label.component';
import { Subscription } from 'rxjs';
import { ProviderNames, normalizeProvider } from 'app/constants/provider-constants';
import { isIndependentNonOwner } from 'app/utils/membership-utils';
import { PopTrayComponent } from 'app/components/ui-kit/popovers/pop-tray/pop-tray.component';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [CommonModule, FormsModule, PageWrapperComponent, SpinnerComponent, SvgLoaderComponent, AlertMessageComponent, MembershipBlockComponent, ToggleRowComponent, BaseInputComponent, PrimaryBtnComponent, SectionLabelComponent, PopTrayComponent],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss'
})
export class ProfileComponent {
  isLoading: boolean = true;
  userProfile: any;
  profileImgURL: any;
  profileImage: any;
  uploadError: string | null = null;
  uploading = false;
  isSavingPref: boolean;
  firstName: string = '';
  lastName: string = '';
  email: string = '';
  isProfileDirty: boolean = false;
  isSavingProfile: boolean = false;
  profileSub: Subscription;
  providerOptions: string[] = [
    ProviderNames.ALL_INDEPENDENT,
    ProviderNames.NETJETS,
    ProviderNames.WHEELS_UP,
    ProviderNames.FLEXJET,
  ];
  providerLabels: Record<string, string> = {
    [ProviderNames.ALL_INDEPENDENT]: 'All Providers',
    [ProviderNames.NETJETS]: 'NetJets',
    [ProviderNames.WHEELS_UP]: 'Wheels Up',
    [ProviderNames.FLEXJET]: 'Flexjet',
  };
  preferredProviders: string[] = [ProviderNames.ALL_INDEPENDENT];
  draftPreferredProviders: string[] = [ProviderNames.ALL_INDEPENDENT];
  showProviderTray: boolean = false;
  isSavingProviders: boolean = false;
  providerTrayActions: any;

  constructor(
    private userService: UserService,
    private router: Router,
  ) {
    this.providerTrayActions = {
      primaryButtons: [
        {
          text: 'Done',
          function: () => this.saveProviderDraft(),
        }
      ],
      closeFunction: () => this.closeProviderTray()
    };
  }

  ngOnInit(): void {
    this.profileSub = this.userService.userProfile$.subscribe(profile => {

      if (profile) {
        this.userProfile = profile;
        this.isLoading = false;
        this.profileImgURL = profile?.profileImgURL ? profile?.profileImgURL + imgIxParams.profile.lg : null;
        this.firstName = profile.firstName;
        this.lastName = profile.lastName;
        this.email = profile.email;
        this.preferredProviders = profile.preferredProviders && profile.preferredProviders.length > 0
          ? profile.preferredProviders.map(normalizeProvider)
          : [ProviderNames.ALL_INDEPENDENT];
        this.isProfileDirty = false;
      }

    });
  }

  ngOnDestroy() {
    this.profileSub.unsubscribe();
  }

  async onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      this.uploading = true;
      this.uploadError = null;

      try {
        const url = await this.userService.uploadProfilePhoto(file);
        this.profileImgURL = url;
      } catch (error) {
        this.uploadError = error.message || 'Failed to upload profile photo.';
      } finally {
        this.uploading = false;
      }
    }
  }

  canEditMembership(membership: any): boolean {
    if (!membership) {
      return false;
    }
    return !isIndependentNonOwner(membership);
  }

  onClickEditMembership(membership) {
    if (!this.canEditMembership(membership)) {
      return;
    }
    const id = membership.id;
    if (!id) {
      return;
    }
    this.router.navigate(['/membership', id]);
  }

  getSettingDescription(setting, type) {
    if (type === 'preference') {
      return preferenceMeta[setting].description;
    } else {
      return preferenceUseMeta[setting].description;
    }
  }


  async onPreferenceChange(change: {
    setting: string;           // e.g. 'pets'
    variant: 'preference' | 'usage'; // which flag
    value: boolean;            // the new true/false
  }) {
    this.isSavingPref = true;
    // 1) Merge locally so the UI updates instantly
    const updatedPrefs = {
      ...this.userProfile.preferences,
      [change.setting]: {
        ...this.userProfile.preferences[change.setting],
        [change.variant]: change.value
      }
    };

    // push the change back into your local model
    this.userProfile.preferences = updatedPrefs;

    // 2) Persist to Firestore
    try {
      await this.userService.updateUserProfile({
        preferences: updatedPrefs
      });
    } catch (err) {
      console.error('Failed to save preference:', err);
      // Optionally revert local change or show UI error
    } finally {
      setTimeout(() => {
         this.isSavingPref = false;
      }, 800);
    }
  }

  checkFormChanges() {
    if (!this.userProfile) return;
    this.isProfileDirty = this.firstName !== this.userProfile.firstName ||
      this.lastName !== this.userProfile.lastName ||
      this.email !== this.userProfile.email;
  }

  isProviderSelected(provider: string): boolean {
    return this.preferredProviders.includes(provider);
  }

  getProviderLabel(provider: string): string {
    return this.providerLabels[provider] || provider;
  }

  get preferredProviderSummary(): string {
    if (this.preferredProviders.includes(ProviderNames.ALL_INDEPENDENT)) {
      return 'All Providers';
    }
    return this.preferredProviders.map(provider => this.getProviderLabel(provider)).join(', ');
  }

  isDraftProviderSelected(provider: string): boolean {
    return this.draftPreferredProviders.includes(provider);
  }

  openProviderTray() {
    if (this.isSavingProviders) {
      return;
    }

    this.draftPreferredProviders = [...this.preferredProviders];
    this.showProviderTray = true;
  }

  closeProviderTray() {
    if (this.isSavingProviders) {
      return;
    }

    this.draftPreferredProviders = [...this.preferredProviders];
    this.showProviderTray = false;
  }

  onProviderDraftToggle(provider: string) {
    if (this.isSavingProviders) {
      return;
    }

    const checked = !this.isDraftProviderSelected(provider);
    if (checked) {
      if (provider === ProviderNames.ALL_INDEPENDENT) {
        this.draftPreferredProviders = [ProviderNames.ALL_INDEPENDENT];
      } else {
        this.draftPreferredProviders = this.draftPreferredProviders.filter(p => p !== ProviderNames.ALL_INDEPENDENT);
        if (!this.draftPreferredProviders.includes(provider)) {
          this.draftPreferredProviders.push(provider);
        }
      }
    } else {
      this.draftPreferredProviders = this.draftPreferredProviders.filter(p => p !== provider);
      if (this.draftPreferredProviders.length === 0) {
        this.draftPreferredProviders = [ProviderNames.ALL_INDEPENDENT];
      }
    }
  }

  async saveProviderDraft() {
    if (this.isSavingProviders) {
      return;
    }

    const normalizedProviders = this.draftPreferredProviders.map(normalizeProvider);
    const current = this.preferredProviders.map(normalizeProvider);
    const unchanged = normalizedProviders.length === current.length &&
      normalizedProviders.every(provider => current.includes(provider));

    if (unchanged) {
      this.showProviderTray = false;
      return;
    }

    this.isSavingProviders = true;
    try {
      this.preferredProviders = normalizedProviders;
      await this.userService.updateUserProfile({ preferredProviders: normalizedProviders });
      this.showProviderTray = false;
    } catch (err) {
      console.error('Failed to save providers:', err);
    } finally {
      setTimeout(() => {
        this.isSavingProviders = false;
      }, 800);
    }
  }

  async saveProfile() {
    this.isSavingProfile = true;
    try {
      await this.userService.updateUserProfile({
        firstName: this.firstName,
        lastName: this.lastName,
        displayName: `${this.firstName} ${this.lastName}`,
        email: this.email
      });
      this.isProfileDirty = false;
    } catch (err) {
      console.error('Failed to save profile info:', err);
    } finally {
      this.isSavingProfile = false;
    }
  }

}
